import React, { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface StockTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  variantId: number;
  companySchema: string;
  depositos: {
    id: number;
    nombre: string;
  }[];
  onSuccess: () => void;
}

export const StockTransferModal: React.FC<StockTransferModalProps> = ({
  isOpen,
  onClose,
  variantId,
  companySchema,
  depositos,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localOrigen, setLocalOrigen] = useState<number | ''>('');
  const [localDestino, setLocalDestino] = useState<number | ''>('');
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!localOrigen || !localDestino || !cantidad) return;
    
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Check available stock in origin
      const { data: stockData, error: stockError } = await supabase
        .schema(companySchema)
        .from('stock')
        .select('cantidad_disponible')
        .eq('local_id', localOrigen)
        .eq('variantes_id', variantId)
        .single();

      if (stockError) throw stockError;
      if (!stockData || stockData.cantidad_disponible < parseInt(cantidad)) {
        throw new Error('No hay suficiente stock en el Local Origen para realizar la transferencia');
      }

      // VERY IMPORTANT: Make sure there's a record in the stock table for destination BEFORE creating the movement
      const { data: destinoStockCheck, error: destinoStockCheckError } = await supabase
        .schema(companySchema)
        .from('stock')
        .select('id')
        .eq('local_id', localDestino)
        .eq('variantes_id', variantId)
        .maybeSingle();

      if (destinoStockCheckError) throw destinoStockCheckError;

      // If destination stock doesn't exist, create it first
      if (!destinoStockCheck) {
        // Insert the new stock record first
        const { error: insertError } = await supabase
          .schema(companySchema)
          .from('stock')
          .insert({
            local_id: localDestino,
            variantes_id: variantId,
            cantidad_disponible: 0,
            cantidad_reservada: 0,
            stock_minimo: 0
          });
        
        if (insertError) {
          console.error('Error creating destination stock:', insertError);
          throw new Error(`Error al crear stock en destino: ${insertError.message}`);
        }
        
        // Make sure the insert completes before continuing - check that it exists now
        const { data: checkInsert, error: checkInsertError } = await supabase
          .schema(companySchema)
          .from('stock')
          .select('id')
          .eq('local_id', localDestino)
          .eq('variantes_id', variantId)
          .single();
          
        if (checkInsertError || !checkInsert) {
          throw new Error('No se pudo confirmar la creación del stock en destino');
        }
      }

      // Now we're sure destination stock exists, create the movement record
      const { error: movimientoError } = await supabase
        .schema(companySchema)
        .from('movimiento_de_stock')
        .insert({
          variante_id: variantId,
          cantidad: parseInt(cantidad),
          motivo,
          local_id_origen: localOrigen,
          local_id_destino: localDestino,
          tipo: 'transferencia',
          fecha: new Date().toISOString(),
          user_id: user.id
        });

      if (movimientoError) {
        console.error('Error creating movement:', movimientoError);
        throw new Error(`Error al crear movimiento: ${movimientoError.message}`);
      }

      // Now update the stock quantities
      // First, decrease origin stock
      const { error: updateOrigenError } = await supabase
        .schema(companySchema)
        .from('stock')
        .update({ 
          cantidad_disponible: stockData.cantidad_disponible - parseInt(cantidad) 
        })
        .eq('local_id', localOrigen)
        .eq('variantes_id', variantId);

      if (updateOrigenError) {
        throw new Error(`Error al actualizar stock origen: ${updateOrigenError.message}`);
      }

      // Then get current destination stock amount and increase it
      const { data: currentDestinoStock, error: destinoStockError } = await supabase
        .schema(companySchema)
        .from('stock')
        .select('cantidad_disponible')
        .eq('local_id', localDestino)
        .eq('variantes_id', variantId)
        .single();

      if (destinoStockError) {
        throw new Error(`Error al obtener stock destino actual: ${destinoStockError.message}`);
      }

      // Update destination stock
      const { error: updateDestinoError } = await supabase
        .schema(companySchema)
        .from('stock')
        .update({ 
          cantidad_disponible: currentDestinoStock.cantidad_disponible + parseInt(cantidad)
        })
        .eq('local_id', localDestino)
        .eq('variantes_id', variantId);

      if (updateDestinoError) {
        throw new Error(`Error al actualizar stock destino: ${updateDestinoError.message}`);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error transferring stock:', err);
      setError(err instanceof Error ? err.message : 'Error al transferir stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Transferir Stock
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Local Origen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Local Origen
              </label>
              <select
                value={localOrigen}
                onChange={(e) => setLocalOrigen(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Seleccionar local</option>
                {depositos.map(deposito => (
                  <option 
                    key={deposito.id} 
                    value={deposito.id}
                    disabled={deposito.id === localDestino}
                  >
                    {deposito.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Local Destino */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Local Destino
              </label>
              <select
                value={localDestino}
                onChange={(e) => setLocalDestino(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Seleccionar local</option>
                {depositos.map(deposito => (
                  <option 
                    key={deposito.id} 
                    value={deposito.id}
                    disabled={deposito.id === localOrigen}
                  >
                    {deposito.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Cantidad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cantidad
              </label>
              <input
                type="number"
                min="1"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Motivo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo
              </label>
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo de la transferencia"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Transfiriendo...
                </>
              ) : (
                'Transferir'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};